import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';

const navLinks = [
  { label: 'Inicio', to: '/' },
  { label: 'Quiénes Somos', to: '/nosotros' },
  { label: 'Rixin Group', to: '/rixin' },
  { label: 'Productos', to: '/productos' },
  { label: 'Diferenciadores', to: '/diferenciadores' },
  { label: 'Contacto', to: '/contacto' },
];

export default function Navbar() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const { totalItems, setIsOpen } = useCart();

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setMobileOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-400 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="flex items-center justify-between h-20 md:h-36">
          <Link
            to="/"
            onClick={isHome ? scrollToTop : undefined}
            className={`flex items-center gap-2.5 md:gap-3 cursor-pointer group ${
              isHome && !scrolled ? 'md:-ml-6 lg:-ml-10' : ''
            }`}
          >
            <img
              src={isHome && !scrolled
                ? 'https://public.readdy.ai/ai/img_res/4412af0d-47c4-4fb9-a4d8-9e8f9cbf751a.png'
                : 'https://public.readdy.ai/ai/img_res/2a810bee-aba7-4718-9c2a-2175c21d1c12.png'
              }
              alt="RESINVENCA Logo"
              className={`h-16 md:h-36 w-auto object-contain transition-all duration-400 select-none ${
                isHome && !scrolled
                  ? 'drop-shadow-[0_0_12px_rgba(255,255,255,0.7)] drop-shadow-[0_0_24px_rgba(255,255,255,0.35)]'
                  : ''
              }`}
              draggable="false"
            />
          </Link>

          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`text-lg font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-primary-600'
                    : 'text-white/90 hover:text-white'
                } ${location.pathname === link.to ? 'font-semibold' : ''}`}
              >
                {link.label}
              </Link>
            ))}

            {/* Cart icon */}
            <button
              onClick={() => setIsOpen(true)}
              className={`relative w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer ${
                scrolled
                  ? 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-600'
                  : 'bg-white/15 text-white hover:bg-white/25'
              }`}
              aria-label="Ver carrito"
            >
              <i className="ri-shopping-cart-2-line text-lg" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-[10px] font-bold">
                  {totalItems}
                </span>
              )}
            </button>

            <Link
              to="/contacto"
              className={`px-6 py-3 rounded-full text-base font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-white/20 text-white border border-white/30 hover:bg-white/30'
              }`}
            >
              Solicitar Cotización
            </Link>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            {/* Mobile cart icon */}
            <button
              onClick={() => setIsOpen(true)}
              className={`relative w-9 h-9 flex items-center justify-center rounded-md cursor-pointer ${
                scrolled ? 'text-foreground-800' : 'text-white'
              }`}
              aria-label="Ver carrito"
            >
              <i className="ri-shopping-cart-2-line text-xl" />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-[9px] font-bold">
                  {totalItems}
                </span>
              )}
            </button>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`w-9 h-9 flex items-center justify-center rounded-md cursor-pointer ${
                scrolled ? 'text-foreground-800' : 'text-white'
              }`}
              aria-label="Menú"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          mobileOpen ? 'max-h-[420px] opacity-100' : 'max-h-0 opacity-0'
        } ${scrolled ? 'bg-background-50' : 'bg-background-50/95 backdrop-blur-md'}`}
      >
        <div className="px-4 py-4 flex flex-col gap-2">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMobileOpen(false)}
              className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors duration-200 cursor-pointer ${
                scrolled
                  ? 'text-foreground-700 hover:bg-background-100'
                  : 'text-foreground-700 hover:bg-background-100'
              } ${location.pathname === link.to ? 'font-semibold' : ''}`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contacto"
            onClick={() => setMobileOpen(false)}
            className="mt-2 py-3 px-4 rounded-lg text-sm font-semibold text-center bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
          >
            Solicitar Cotización
          </Link>
        </div>
      </div>
    </nav>
  );
}